import CrossIcon from "@assets/icons/cross.svg";
import { useI18n } from "@core/i18n/i18n";
import { formatDayColumn } from "@domain/program/program";
import { DayIndex } from "@domain/program/program-days";
import { ComplianceGraphData, getFakeComplianceGraphData } from "@domain/progress/progress-data";
import styled from "@emotion/styled";
import { ConditionalSkeleton } from "@ui/components/common/conditional-skeleton";
import { ContentWithTooltip } from "@ui/components/common/content-with-tooltip";
import { Graph, Scale } from "@ui/pages/patient-details/components/progress-tab/graph";
import { useComplianceGraphData } from "@ui/pages/patient-details/components/progress-tab/hooks/use-progress-data";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNil } from "@utils/assert";
import React, { useCallback, useMemo } from "react";

const NUMBER_OF_WEEKS_DISPLAYED = 8;
const EMPTY_X_LABEL_VALUE = "";

const SCALE: { x: Scale; y: Scale } = {
	x: { min: 0, max: NUMBER_OF_WEEKS_DISPLAYED - 1 },
	y: { min: 0, max: 100, tick: 5 },
};

export const ComplianceGraph: React.FC<{
	patientId: string;
	programId: string | undefined;
	isEmpty: boolean;
	isLoading: boolean;
}> = ({ patientId, programId, isEmpty, isLoading }) => {
	const { loading, complianceGraphData } = useComplianceGraphData(patientId, programId);
	const definedComplianceGraphData = useMemo(
		() => complianceGraphData ?? getFakeComplianceGraphData(),
		[complianceGraphData],
	);

	return (
		<ComplianceGraphView
			isEmpty={isEmpty}
			isLoading={
				isLoading ||
				(complianceGraphData === undefined && loading) ||
				(!!programId && complianceGraphData === undefined)
			}
			complianceGraphData={definedComplianceGraphData}
		/>
	);
};

const ComplianceGraphView: React.FC<{
	complianceGraphData: ComplianceGraphData;
	isEmpty: boolean;
	isLoading?: boolean;
}> = ({ complianceGraphData, isEmpty, isLoading = false }) => {
	const { format, formatPercent } = useI18n();
	const skeletonProps = { active: isEmpty || isLoading, loading: isLoading };
	const displayScales = useMemo(() => !isLoading && !isEmpty, [isEmpty, isLoading]);

	const data = useMemo(() => complianceGraphData.map(week => week.compliancePercentage), [complianceGraphData]);

	const formatDotTooltip = useCallback(
		(x: number) => (
			<TooltipContent>
				{format("patientDetails.progress.complianceGraphSection.tooltip.v1_6", {
					x: complianceGraphData[x]?.weekIndex,
				})}
				<TooltipDays>
					{complianceGraphData[x]?.days.map(day => (
						<DayChecked key={day.day} dayIndex={day.day} checked={day.completed} />
					))}
				</TooltipDays>
			</TooltipContent>
		),
		[complianceGraphData, format],
	);
	const formatScaleX = useCallback(
		(x: number) => {
			const week = complianceGraphData[x];
			if (isNil(week)) return EMPTY_X_LABEL_VALUE;
			const { startDate, endDate } = week;
			return format("patientDetails.progress.complianceGraphSection.graphXAxisDate.oneMonth", {
				month: startDate.format("MMM"),
				day1: startDate.format("DD"),
				day2: endDate.format("DD"),
			});
		},
		[complianceGraphData, format],
	);

	return (
		<Section>
			<HeaderContainer>
				<ContentWithTooltip
					tooltip={format("patientDetails.progress.complianceSection.tooltip.v2_0")}
					skeleton={skeletonProps.active ? skeletonProps : undefined}
				>
					<div className="p-size-m-bold">{format("patientDetails.progress.complianceGraphSection.title")}</div>
				</ContentWithTooltip>
				<ConditionalSkeleton {...skeletonProps} skeletonStyle={{ borderRadius: 4 }}>
					<Cta className="p-size-xs-semi-bold">{format("patientDetails.progress.complianceGraphSection.scaleCta")}</Cta>
				</ConditionalSkeleton>
			</HeaderContainer>
			<GraphContainer>
				<Graph
					id="compliance"
					displayScales={displayScales}
					scale={SCALE}
					data={data}
					renderDotTooltipContent={formatDotTooltip}
					formatValue={formatPercent}
					formatScaleX={formatScaleX}
				/>
			</GraphContainer>
		</Section>
	);
};

const Section = styled.section`
	${stack(16, undefined, "stretch")}
`;

const HeaderContainer = styled.div`
	${row(undefined, "spacebetween", "center")};
	@media ${physioMeMobileBreakpoint} {
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
	}
`;

const Cta = styled.div`
	padding: 4px 8px;
	border-radius: 4px;
	background-color: ${colors.grey10};
	color: ${colors.grey};
`;

const TooltipContent = styled.div`
	${stack()};
`;

const TooltipDays = styled.div`
	display: grid;
	grid-template-columns: auto 1fr;
`;

const DayChecked: React.FC<{ dayIndex: DayIndex; checked: boolean }> = ({ dayIndex, checked }) => (
	<>
		<span className="p-size-xs-semi-bold">{formatDayColumn(dayIndex, "short")}</span>
		<DayContainer>
			{checked ? (
				<i className="pi pi-check" style={{ color: colors.validation, fontSize: "0.6rem" }} />
			) : (
				<CrossIcon width={12} height={12} color={colors.rejection} />
			)}
		</DayContainer>
	</>
);

const DayContainer = styled.div`
	${row(undefined, "flexstart", "center")};
	color: ${colors.white};
	padding-left: 5px;
`;

const GraphContainer = styled.div`
	border-radius: 8px;
	border: 1px solid ${colors.grey25};
`;
